import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { TransportService } from '../../services/transport.service';
import { TransportOutConfiguration } from '../../models/transport-configuration';
import { computedAsync } from 'src/app/shared/signal-utilities';
import { PartnerService } from 'src/app/partner/services/partner.service';
import { PartnerLoadOptions, PartnerSystem } from 'src/app/partner/models';
import { TransportType } from '../../models/transport-type';
import { PartnerSystemService } from 'src/app/partner/services/partner-system.service';
import { Router } from '@angular/router';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { ConfigurationService } from 'src/app/configuration/services/configuration.service';
import { TransportOutOverrideDialogComponent } from '../transport-out-override-dialog/transport-out-override-dialog.component';
import { TransportOutOverrideService } from '../../services/transport-out-override.service';
import { PageHeaderComponent } from '../../../controls/components/advanced/page-header/page-header.component';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { RestrictedSearchCalloutComponent } from '../../../shared/restricted-search/restricted-search.component';
import { TransportOutLocationsGridComponent } from '../transport-out-locations-grid/transport-out-locations-grid.component';

@Component({
    selector: 'app-transport-out-locations-list',
    templateUrl: './transport-out-locations-list.component.html',
    styleUrls: ['./transport-out-locations-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PageHeaderComponent, SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, RestrictedSearchCalloutComponent, TransportOutLocationsGridComponent]
})
export class TransportOutLocationsListComponent implements OnInit {
  private router = inject(Router);
  private loadingPanelService = inject(LoadingPanelService);
  private modalService = inject(GenericModalService);
  private toastService = inject(ToastService);
  private currentUserService = inject(CurrentUserService);
  private configurationService = inject(ConfigurationService);
  private partnerService = inject(PartnerService);
  private transportService = inject(TransportService);
  private partnerSystemService = inject(PartnerSystemService);
  private transportOutOverrideService = inject(TransportOutOverrideService);

  allowViewing = this.currentUserService.hasPermission('Transport_View_OutgoingLocation_Details');
  allowAdding = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Create');
  allowCopying = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Copy');
  allowDeleting = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_Delete');
  allowManageGlobal = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_ManageGlobal');
  allowShowLog = this.currentUserService.hasPermission('Transport_View_OutgoingLocation_Log');
  private loading = signal(0);

  locations = signal<TransportOutConfiguration[]>([]);
  restricted = signal(false);
  selectedLocations = signal<TransportOutConfiguration[]>([]);
  transportTypes = signal<TransportType[]>([]);
  partnerSystems = signal<PartnerSystem[]>([]);

  partners = computedAsync(async () => {
    const partnerIds = this.locations()
      .filter(e => e.PartnerId)
      .map(e => e.PartnerId)
      .filter((val, idx, self) => self.indexOf(val) === idx) // distinct;
      .sort((a, b) => a - b);
    return partnerIds.length > 0 ? await this.partnerService.getLightPartners(partnerIds) : [];
  });

  canViewLocations = computed(() => this.selectedLocations().length > 0);
  canManageLocations = computed(() => this.selectedLocations().every(e => !!e.PartnerId || this.allowManageGlobal) && (this.selectedLocations().length > 0));
  canExportLocations = computed(() => this.canManageLocations());
  canUnblockLocations = computed(() => this.canManageLocations() && this.selectedLocations().every(e => e.BlockedByImport));
  hasblockedLocations = computed(() => this.locations().some(e => e.BlockedByImport));
  transportOutOverrideId = signal(-1);

  constructor() {
    // This effect shows the loading panel when loading is in progress
    let previous = 0;
    effect(() => {
      if ((this.loading() === 1) && (previous === 0)) {
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
      } else if ((this.loading() === 0) && (previous === 1)) {
        this.loadingPanelService.hideLoadingPanel();
      }
      previous = this.loading();
    });
  }

  ngOnInit(): void {
    this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.beginLoading();

      this.transportOutOverrideId.set(-1);
      const [locationResult, transportTypes, partnerSystems, transportOutOverride] = await Promise.all([
        this.transportService.getAllTransportOutConfigurations(),
        this.transportService.getTransportTypes(this.transportService.transportTypesOut),
        this.partnerSystemService.getAllPartnerSystems(),
        this.configurationService.getEffectiveSettingValue('TransportOut_Override'),
      ]);

      this.locations.set(locationResult.TransportOutConfigurations);
      this.restricted.set(locationResult.IsTransportOutConfigurationSearchResultRestricted);
      this.transportTypes.set(transportTypes);
      this.partnerSystems.set(partnerSystems);
      this.transportOutOverrideId.set(Number(transportOutOverride || 0));
    }
    finally {
      this.endLoading();
    }
  }

  beginLoading(): void {
    this.loading.update(n => n + 1);
  }

  endLoading(): void {
    this.loading.update(n => n - 1);
  }

  viewLocation(location: TransportOutConfiguration, autoEdit?: boolean): void {
    this.viewLocations([location], autoEdit);
  }

  viewLocations(locations: TransportOutConfiguration[], autoEdit?: boolean): Promise<boolean> {
    const locationIds = locations
      .map(e => e.Id)
      .filter((val, idx, self) => self.indexOf(val) === idx) // distinct;
      .sort((a, b) => a - b);
    return this.router.navigate(['transport-locations/outgoing/details', { locationIds }], { state: { autoEdit } });
  }

  async exportLocations(locations: TransportOutConfiguration[]): Promise<void> {
    await this.transportService.exportTransportOutConfigurations(locations.map(e => e.Id));
  }

  async unblockLocations(locations: TransportOutConfiguration[]): Promise<void> {
    try {
      if (locations.length === 1) {
        await this.modalService.confirmDirect(`Unblock ${locations[0].Name}`, 'Do you want to unblock this location?');
      }
      else {
        await this.modalService.confirmDirect('Unblock', `Do you want to unblock ${locations.length} locations?`);
      }

      try {
        const unblockedLocations = await this.transportService.unblockTransportOutConfigurations(locations.map(e => e.Id).sort((a, b) => a - b));
        this.locations.update(e => e.map(location => {
          const unblockedLocation = unblockedLocations.find(u => u.Id === location.Id);
          return unblockedLocation ?? location;
        }));

        this.toastService.showToast('Locations unblocked successfully.', ToastType.success);
      }
      catch {
        this.toastService.showToast('Failed to unblock location.', ToastType.error);
      }
    }
    catch {
      // User cancelled
    }
  }

  addLocation(): void {
    this.router.navigate(['transport-locations/outgoing/details', { locationIds: [0] }]);
  }

  async copyLocation(location: TransportOutConfiguration): Promise<void> {
    try {
      await this.modalService.confirmDirect(`Copy ${location.Name}`, 'Do you want to make a copy of this location?');
      try {
        const clonedLocation = await this.transportService.cloneTransportOutConfiguration(location);
        this.locations.set([...this.locations(), clonedLocation]);

        this.viewLocation(clonedLocation, true);
        this.toastService.showToast('Location copied successfully.', ToastType.success);
      }
      catch {
        this.toastService.showToast('Failed to copy location.', ToastType.error);
      }
    }
    catch {
      // User cancelled
    }
  }

  async deleteLocation(location: TransportOutConfiguration): Promise<void> {
    try {
      await this.modalService.confirmDirect(`Delete ${location.Name}`, 'Are you sure you want to delete this location?');
      try {
        await this.transportService.deleteTransportOutConfiguration(location.Id);
        this.locations.set(this.locations().filter(e => e.Id !== location.Id));
        this.toastService.showToast('Location deleted successfully.', ToastType.success);
      }
      catch {
        this.toastService.showToast('Failed to delete location.', ToastType.error);
      }
    }
    catch {
      // User cancelled
    }
  }

  selectionChanged(locations: TransportOutConfiguration[]): void {
    this.selectedLocations.set(locations);
  }

  overrideLocations = async () => {
    try {
      const modalSettings = new ModalSettingsTemplate('xl', 'static');
      const overrideId = await this.modalService.openComponentWithInitialize<number, TransportOutOverrideDialogComponent>(TransportOutOverrideDialogComponent, modalSettings, dialog => {
        dialog.overrideId.set(this.transportOutOverrideId());
      });

      try {
        if (overrideId) {
          this.transportOutOverrideId.set(overrideId);
          await this.configurationService.saveEffectiveSettingValue('TransportOut_Override', overrideId.toString());
          this.toastService.showToast('Locations overridden successfully.', ToastType.success);
        }
        else {
          this.transportOutOverrideId.set(0);
          await this.configurationService.saveEffectiveSettingValue('TransportOut_Override', '');
          this.toastService.showToast('Override stopped.', ToastType.success);
        }
      }
      catch (err) {
        console.error(err);
        this.toastService.showToast('Failed to override locations.', ToastType.error, 5000);
      }
      finally {
        await this.transportOutOverrideService.checkOverrideStatus();
      }
    }
    catch {
      // User cancelled
    }
  };
}
